import React, { useMemo } from 'react';
import { useHistory } from 'react-router';
import { useToggle } from 'react-use';
import { Menu } from 'antd';
import { EllipsisOutlined, PlusOutlined } from '@ant-design/icons';
import map from 'lodash/map';
import replace from 'lodash/replace';
import first from 'lodash/first';

import useGettingCalendars from './hooks/useGettingCalendars';
import SidebarItem from '@components/layout/Sidebar/SidebarItem';
import EditPlaylist from '@components/snippets/EditPlaylist';
import CalendarIcon from '@assets/img/calendar.svg';
import { Path } from '../../routes';
import CreateCalendar from './CreateCalendar';
import useCreateCalendar from './hooks/useCreateCalendar';
import useDeleteCalendar from './hooks/useDeleteCalendar';
import useCopyCalendar from './hooks/useCopyCalendar';
import useRenameCalendar from './hooks/useRenameCalendar';
import cn from 'classnames';
import toString from 'lodash/toString';
import pathToRegexp from 'path-to-regexp';
import { useTranslation } from 'react-i18next';
import noop from 'lodash/noop';
import memoize from 'lodash/memoize';

const { SubMenu } = Menu;

function CalendarMenu({ onSelect, openId, routeName, routeId, ...props }) {
  const { t } = useTranslation();
  const history = useHistory();
  const [showCreateCalendar, toggleCreateCalendar] = useToggle(false);
  const { calendars, onFetchCalendars } = useGettingCalendars();

  const onSuccess = (eventName) => ({ id } = {}) => {
    onFetchCalendars();

    if (showCreateCalendar) toggleCreateCalendar();
    if (eventName === 'create') {
      history.push(pathToRegexp.compile(Path.CALENDAR)({ id }));
    } else if (eventName === 'delete') {
      onFetchCalendars({
        onSuccess: (calendars) =>
          history.push(
            pathToRegexp.compile(Path.CALENDAR)({ id: first(calendars).id })
          ),
      });
    } else {
      onFetchCalendars();
    }
  };
  const { onCreateCalendar, createLoading } = useCreateCalendar({
    onSuccess: onSuccess('create'),
  });
  const { onDeleteCalendar } = useDeleteCalendar({
    onSuccess: onSuccess('delete'),
  });
  const { onCopyCalendar } = useCopyCalendar({ onSuccess: onSuccess('copy') });
  const { onRenameCalendar } = useRenameCalendar({
    onSuccess: onSuccess('rename'),
  });

  const renderExtra = memoize((id, name) => {
    return (
      <EditPlaylist
        onRename={onRenameCalendar(id)}
        onCopy={onCopyCalendar(name)}
        name={name}
        onDelete={onDeleteCalendar(id)}
        rotate={false}
        withoutColor
        className="sidebar__nav-item-extra"
        icon={<EllipsisOutlined className="sidebar__nav-item-extra-icon" />}
      />
    );
  });

  const menu = useMemo(() => {
    return map(calendars, ({ id, name }, index) => (
      <SidebarItem
        key={index}
        id={id}
        className={cn('sidebar__nav-item--margin-left', {
          'ant-menu-item-selected':
            routeName === 'calendar' && toString(id) === toString(routeId),
        })}
        href={replace(Path.CALENDAR, ':id', id)}
        text={name}
        extra={renderExtra(id, name)}
      />
    ));
  }, [routeName, routeId, calendars]);

  return (
    <SubMenu
      key="item_2"
      onTitleClick={onSelect}
      onSelect={noop}
      {...props}
      className={cn('sidebar__nav-submenu-title', {
        'ant-menu-item-selected': openId === 'calendar',
      })}
      title={
        <div>
          <CalendarIcon className="sidebar__nav-icon" width={16} height={16} />
          <span className="sidebar__nav-item-text">{t('calendar')}</span>
        </div>
      }
    >
      <CreateCalendar
        t={t}
        loading={createLoading}
        onFinish={onCreateCalendar}
        visible={showCreateCalendar}
        toggle={toggleCreateCalendar}
      />

      <SidebarItem
        id="calendar-create"
        className="sidebar__nav--create"
        icon={
          <PlusOutlined
            className="sidebar__nav-icon--plus"
            width={12}
            height={12}
          />
        }
        onClick={toggleCreateCalendar}
        text={t('create-calendar')}
      />
      {menu}
    </SubMenu>
  );
}

CalendarMenu.whyDidYouRender = true;

export default CalendarMenu;
